'use client';

import { useEffect } from 'react';

import { reloadPage } from '@/lib/browser-navigation';
import { useTrackEvent } from '@/lib/posthog';

const CHUNK_PATH = '/_next/static/chunks/';
// Any asset under our Next.js static output (chunks live in .../chunks/, CSS in
// .../css/) — the marker of a resource we actually serve and can recover by
// reloading onto the live deployment.
const APP_STATIC_PATH = '/_next/';
// Patterns distinctive enough that a chunk failure is unambiguous from the
// message alone (still gated on app origin — see below).
const DISTINCTIVE_CHUNK_PATTERNS = /ChunkLoadError|Loading chunk|Loading CSS chunk/i;
// Generic fetch error — only a chunk match in message or URL makes it a chunk failure.
const GENERIC_FETCH_PATTERN = /Failed to fetch/i;
// Script/style asset URL or root-relative path embedded in an error, e.g.
// "https://host/static/identify.js?v=1" or "/_next/static/chunks/4696.js".
// Used to tell whether a chunk-shaped error blames one of our own assets or a
// foreign (injected/extension) resource.
const ASSET_URL_PATTERN = /(?:https?:\/\/[^\s)'"]+|\/[^\s)'"]*)\.(?:js|mjs|css)/gi;
const SESSION_KEY = 'chunk_reload_attempted';

/**
 * Returns true if the (message, urlContext) pair describes a Next.js chunk
 * fetch failure — i.e. the kind of error we want to recover from by reloading
 * the page so the user lands on the live deployment.
 *
 * - Distinctive webpack patterns (ChunkLoadError, Loading chunk, Loading CSS
 *   chunk) identify a chunk-shaped error on message match alone.
 * - Generic "Failed to fetch" additionally requires a chunk path in either the
 *   URL context or the message to avoid reloading on unrelated fetch failures.
 * - In both cases, if the error names any concrete asset URL, at least one must
 *   live under our /_next/ output. A foreign script (browser extension /
 *   injected third-party tag) can throw a ChunkLoadError-shaped error for an
 *   asset we don't serve (e.g. /static/identify.js); reloading can't fetch a
 *   resource that isn't ours, so we refuse. Errors that name no asset at all
 *   (bare "Failed to fetch", Safari/Firefox rejections) still pass — in a
 *   Next.js app those are overwhelmingly our own chunk loads.
 *
 * Exported for reuse by ErrorBoundary (src/components/error-boundary.tsx),
 * which recovers from the same failure class thrown during React's render
 * phase, and for unit tests.
 */
export function isChunkLoadFailure(message: string, urlContext: string): boolean {
  const chunkShaped =
    DISTINCTIVE_CHUNK_PATTERNS.test(message) ||
    (GENERIC_FETCH_PATTERN.test(message) &&
      (urlContext.includes(CHUNK_PATH) || message.includes(CHUNK_PATH)));
  if (!chunkShaped) return false;
  const assets = `${message} ${urlContext}`.match(ASSET_URL_PATTERN);
  if (assets && !assets.some((asset) => asset.includes(APP_STATIC_PATH))) return false;
  return true;
}

/**
 * Best-effort extraction of the failed chunk's URL for telemetry, given the
 * same (message, urlContext) pair passed to isChunkLoadFailure. Prefers
 * urlContext when it already names a chunk path (the window `error` event's
 * `filename`); otherwise falls back to a chunk URL embedded in the message
 * itself. `urlContext` may legitimately be an empty string when no such
 * context is available (e.g. ErrorBoundary has no `filename`-equivalent) —
 * that's not an error, it just means only the message is searched.
 */
export function extractChunkUrl(message: string, urlContext: string): string {
  if (urlContext.includes(CHUNK_PATH)) return urlContext;
  const match = message.match(/https?:\/\/\S*\/_next\/static\/chunks\/\S+/);
  return match?.[0] ?? '';
}

/**
 * Claims the one-shot chunk-reload slot for this browser session.
 *
 * Returns true if this call successfully claimed the slot (caller should
 * proceed with recovery), false if it was already claimed or sessionStorage
 * is unavailable (Safari private mode, quota exceeded) — in which case the
 * caller must not reload, to avoid an unguarded loop.
 *
 * Shared between ChunkLoadReloadGuard (window-level failures) and
 * ErrorBoundary (render-phase failures) so both draw from the same one-shot
 * budget and can't double-reload in the same session.
 */
export function claimChunkReloadOnce(): boolean {
  try {
    if (window.sessionStorage.getItem(SESSION_KEY)) return false;
    window.sessionStorage.setItem(SESSION_KEY, '1');
    return true;
  } catch {
    // sessionStorage unavailable — refuse to reload to avoid an unguarded loop.
    return false;
  }
}

/**
 * Catches lazy-loaded chunk fetch failures (TypeError: Failed to fetch on
 * `/_next/static/chunks/*`) — usually triggered when a user's tab outlives
 * the Vercel deployment that originally served its HTML — and recovers by
 * performing a one-shot full-page reload so they land on the live deployment.
 *
 * Guarded by `sessionStorage.chunk_reload_attempted` so a degraded edge can't
 * put a user into a reload loop. If sessionStorage is unavailable (Safari
 * private mode, quota exceeded), the guard refuses to reload at all — better
 * to leave the broken UI than risk an unguarded loop.
 *
 * Renders nothing.
 */
export function ChunkLoadReloadGuard(): null {
  const { trackEvent } = useTrackEvent();

  useEffect(() => {
    const tryReload = (message: string, urlContext: string) => {
      if (!isChunkLoadFailure(message, urlContext)) return;
      // If the user is offline the reload will show the browser's "No Internet"
      // page — worse UX than staying on the broken-chunk UI.
      if (!navigator.onLine) return;
      if (!claimChunkReloadOnce()) return;
      try {
        trackEvent(
          'CHUNK_LOAD_RELOAD',
          {
            failed_url: extractChunkUrl(message, urlContext),
            current_url: window.location.href,
          },
          { source: 'client' }
        );
      } catch {
        // PostHog SDK must never block reload recovery.
      }
      reloadPage();
    };

    const onError = (event: ErrorEvent) => {
      tryReload(event.message, event.filename);
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as unknown;
      const message =
        reason instanceof Error
          ? `${reason.name}: ${reason.message}`
          : typeof reason === 'string'
            ? reason
            : '';
      // Chrome includes the resource URL in rejection messages; Safari/Firefox
      // emit a bare "Failed to fetch" without it. When the message contains no
      // chunk URL, synthesise CHUNK_PATH as urlContext so the predicate accepts
      // bare "Failed to fetch" rejections — unhandled fetch rejections in a
      // Next.js app are almost always chunk/module loads, not API calls
      // (which are always caught and never left as unhandled rejections).
      const urlContext = message.includes(CHUNK_PATH) ? '' : CHUNK_PATH;
      tryReload(message, urlContext);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, [trackEvent]);

  return null;
}
